import { useEffect } from 'react';
import { setConsent, getConsent } from '@/lib/consent';

// Termly fires a consent callback via window.Termly.on('consent', fn).
// The payload is an object whose keys are category names and values are
// 'active' | 'inactive'. We map the 'analytics' category to our own
// consent.ts so that PostHog (P2-16) can gate on getConsent().analytics.
//
// window.Termly is set asynchronously after the resource-blocker script loads,
// so we retry with a short polling loop rather than expecting it on first render.

declare global {
  interface Window {
    Termly?: {
      on: (event: string, callback: (payload: Record<string, string>) => void) => void;
    };
  }
}

function syncFromTermly(payload: Record<string, string>) {
  const analyticsActive =
    payload['analytics'] === 'active' || payload['performance'] === 'active';
  setConsent(analyticsActive);
}

export function TermlyConsentBridge() {
  useEffect(() => {
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // 10 seconds total

    const register = () => {
      if (window.Termly) {
        window.Termly.on('consent', syncFromTermly);

        // Also sync immediately if the user already made a decision in a prior visit.
        // Termly fires 'consent' on load when a stored preference exists, so this
        // is mainly a safety net for the case where the event fired before we registered.
        if (!getConsent()) {
          // No local consent record yet — wait for Termly's banner interaction.
        }
        return true;
      }
      return false;
    };

    if (!register()) {
      const interval = setInterval(() => {
        attempts++;
        if (register() || attempts >= MAX_ATTEMPTS) {
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, []);

  return null;
}
